import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Chip,
    Alert,
    CircularProgress,
    Container,
    Paper,
    AppBar,
    Toolbar,
    IconButton
} from '@mui/material';
import { ArrowBack, School, Description, AccessTime } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../users/AuthContext';
import { APIApplicationFormListPublished } from '../../utilities/APIs';
import useCustomToast from '../../hooks/useToast';

const ApplicantFormList = () => {
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { makeAuthenticatedRequest, user, logout } = useAuth();
    const showToast = useCustomToast();

    useEffect(() => {
        fetchPublishedForms();
    }, []);

    const fetchPublishedForms = async () => {
        try {
            setLoading(true);
            const response = await makeAuthenticatedRequest(APIApplicationFormListPublished);
            
            if (response.data && Array.isArray(response.data)) {
                setForms(response.data);
                console.log("response.data"+response.data)
                showToast('Data Fetching', 'Data Fetched very well', 'success');

            } else {
                setForms([]);
            }
        } catch (err) {
            console.error('Error fetching forms:', err);
            setError('Failed to load application forms');
            showToast('Error', 'Failed to load application forms', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleFormSelect = (form) => {
        navigate(`/applicant/form/${form.id}`, { 
            state: { 
                formData: form,
                userType: 'APPLICANT'
            }
        });
    };

    const handleBackToDashboard = () => {
        navigate('/applicant/dashboard');
    };

    const handleLogout = () => {
        logout();
        navigate('/applicant/login');
    };

    const getFormTypeColor = (type) => {
        switch (type?.toLowerCase()) {
            case 'undergraduate':
                return 'primary';
            case 'graduate':
                return 'secondary';
            case 'shortcourse':
                return 'success';
            default:
                return 'default';
        }
    };

    const getFormTypeIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'undergraduate':
                return <School />;
            case 'graduate':
                return <School />;
            case 'shortcourse':
                return <Description />;
            default:
                return <Description />;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'grey.50' }}>
            {/* Header */}
            <AppBar position="static" sx={{ backgroundColor: 'primary.main' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleBackToDashboard}
                        sx={{ mr: 2 }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        Available Application Forms
                    </Typography>
                    <Typography variant="body2" sx={{ mr: 2 }}>
                        Welcome, {user?.firstName || user?.username}
                    </Typography>
                    <Button color="inherit" onClick={handleLogout}>
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {forms.length === 0 && !loading && !error && (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Application Forms Available
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            There are currently no published application forms. Please check back later.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={handleBackToDashboard}
                            sx={{ mt: 2 }}
                        >
                            Back to Dashboard
                        </Button>
                    </Paper>
                )}

                <Grid container spacing={3}>
                    {forms.map((form) => (
                        <Grid item xs={12} md={6} lg={4} key={form.id}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'transform 0.2s ease-in-out, elevation 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        elevation: 8
                                    }
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        {getFormTypeIcon(form.type)}
                                        <Typography variant="h6" component="h2" sx={{ ml: 1, flexGrow: 1 }}>
                                            {form.name || form.formName}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ mb: 2 }}>
                                        <Chip 
                                            label={form.type?.toUpperCase() || 'FORM'}
                                            color={getFormTypeColor(form.type)}
                                            size="small"
                                            sx={{ mr: 1 }}
                                        />
                                        <Chip 
                                            label="Published"
                                            color="success"
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>

                                    {form.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {form.description}
                                        </Typography>
                                    )}

                                    {form.createdAt && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <AccessTime sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary">
                                                Created: {new Date(form.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                                
                                <Box sx={{ p: 2, pt: 0 }}>
                                    <Button 
                                        variant="contained" 
                                        fullWidth
                                        onClick={() => handleFormSelect(form)}
                                        sx={{ 
                                            backgroundColor: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark'
                                            }
                                        }}
                                    >
                                        Start Application
                                    </Button>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default ApplicantFormList;
